import NetInfo from '@react-native-community/netinfo';
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { cmsApiSlice, useGetGlobalConfigQuery } from 'services/cms';
import { RootState } from 'store/index';
import { Platform } from 'utils/platform';

type GlobalContextProps = GlobalState & {
  setColor: (payload: any) => void;
};

type GlobalState = Partial<GlobalConfig> & {
  dataIsLoading: boolean;
  hasInternet?: boolean;
  dataLogin?: { skipAuthenticated?: boolean };
  colors?: any;
  isScrollingDown?: boolean;
  hasError?: boolean;
  handleScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  setValueOffSetScroll?: (value: number) => void;
  offSetScroll?: number;
  isKidState?: boolean;
};

type GlobalAction =
  | { type: 'SET_DATA_LOGIN'; payload: { skipAuthenticated?: boolean } }
  | {
      type: 'SET_DATA_HEADER';
      payload: Pick<GlobalState, 'HeaderMenu'>;
    }
  | {
      type: 'SET_DATA_GENERAL';
      payload: any;
    }
  | {
      type: 'SET_DATA_ISLOADING';
      payload: boolean;
    }
  | {
      type: 'SET_DATA';
      payload?: GlobalConfig;
    }
  | { type: 'HAS_INTERNET'; payload: boolean | undefined }
  | {
      type: 'SET_COLORS';
      payload?: any;
    }
  | { type: 'SET_IS_KID'; payload: boolean };

function reducer(state: GlobalState, action: GlobalAction) {
  switch (action.type) {
    case 'SET_DATA_HEADER':
      return { ...state, dataHeader: action.payload };
    case 'SET_DATA_LOGIN':
      return { ...state, dataLogin: action.payload };
    case 'SET_DATA_GENERAL':
      return { ...state, dataGeneral: action.payload };
    case 'SET_DATA_ISLOADING':
      return { ...state, dataIsLoading: action.payload };
    case 'SET_DATA':
      return { ...state, ...action.payload, dataIsLoading: false };
    case 'HAS_INTERNET':
      return { ...state, hasInternet: action.payload };
    case 'SET_COLORS':
      return { ...state, colors: action.payload, dataIsLoading: false };
    case 'SET_IS_KID':
      return { ...state, isKidState: action.payload };
    default:
      return state;
  }
}

const GlobalContext = createContext({} as GlobalContextProps);

export const GlobalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { data, isLoading } = useGetGlobalConfigQuery();
  const reduxDispatch = useDispatch();
  const { isKid } = useSelector((state: RootState) => state.profile);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [offSetScroll, setOffSetScroll] = useState(0);
  const prevHasInternetRef = useRef<boolean | null>(null);

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const currentOffsetY = event.nativeEvent.contentOffset.y;
    const contentHeight = event.nativeEvent.contentSize.height;
    const viewportHeight = event.nativeEvent.layoutMeasurement.height;

    const tenPercentHeight = (contentHeight - viewportHeight) * 0.2;

    if (currentOffsetY >= tenPercentHeight) {
      setIsScrollingDown(true);
    } else {
      setIsScrollingDown(false);
    }
  };

  const setValueOffSetScroll = (value: number) => {
    setOffSetScroll(value);
  };

  const [state, dispatch] = useReducer(reducer, {
    dataIsLoading: true,
    hasError: false,
  });

  const setColor = (payload: any) => {
    dispatch({ type: 'SET_COLORS', payload });
  };

  useEffect(() => {
    dispatch({ type: 'SET_IS_KID', payload: isKid });
  }, [isKid]);

  useEffect(() => {
    if (Platform.isWebTV) {
      const handleOnlineStatus = () => {
        dispatch({ type: 'HAS_INTERNET', payload: window?.navigator?.onLine ?? true });
      };

      handleOnlineStatus();

      window.addEventListener('online', handleOnlineStatus);
      window.addEventListener('offline', handleOnlineStatus);

      return () => {
        window.removeEventListener('online', handleOnlineStatus);
        window.removeEventListener('offline', handleOnlineStatus);
      };
    } else {
      return NetInfo.addEventListener(({ isConnected }) => {
        if (state.hasInternet === false && isConnected) {
          reduxDispatch(cmsApiSlice.util.resetApiState());
        }
        dispatch({ type: 'HAS_INTERNET', payload: isConnected ?? undefined });
      });
    }
  }, [state.hasInternet]);

  useEffect(() => {
    dispatch({ type: 'SET_DATA', payload: data });
    dispatch({
      type: 'SET_DATA_LOGIN',
      payload: { skipAuthenticated: data?.LoginScreen?.skipAuthenticated },
    });
  }, [data, isLoading]);

  return (
    <GlobalContext.Provider
      value={{
        ...state,
        setColor,
        handleScroll,
        isScrollingDown,
        offSetScroll,
        setValueOffSetScroll,
        prevHasInternetRef,
      }}>
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalConfig = () => useContext(GlobalContext);
